import React, { useState, useMemo, useEffect, useRef } from 'react';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, storage } from '../firebase/config';
import { Offer } from '../types';
import { calculatePtcScore, buildPtcInputFromOffer, PTCCalculationInput } from '../services/ptcCalculator';
import './OfferEditModal.css';

interface OfferEditModalProps {
  offer: Offer;
  lenderExperienceYears: number | null;
  onClose: () => void;
  onSaved: (updatedOffer: Offer) => void;
}

interface EditFormState {
  // _meta
  has_pre_approval: string; // 'true' | 'false'
  // pre_approval
  pa_lender_name: string;
  pa_lender_phone: string;
  pa_loan_officer_name: string;
  pa_loan_officer_nmls: string;
  pa_loan_officer_email: string;
  pa_loan_type: string;
  pa_base_loan_amount: string;
  pa_max_purchase_price: string;
  pa_letter_date: string;
  pa_expiration_date: string;
  // transaction
  t_final_sales_price: string;
  t_earnest_money_deposit: string;
  t_closing_date: string;
  t_offer_date: string;
  // financing
  f_payment_method: string;
  f_mortgage_type: string;
  f_mortgage_amount: string;
  f_down_payment_amount: string;
  f_down_payment_percent: string;
  f_seller_concession_amount: string;
  // contingencies
  c_appraisal: string;              // 'true' | 'false' | ''
  c_inspection_waived: string;      // 'true' | 'false' | ''
  c_sale_of_buyers_property: string;
  c_mortgage: string;
  // property
  prop_address: string;
  prop_city: string;
  prop_state: string;
  prop_zip: string;
  // buyer agent
  ag_agent_name: string;
  ag_email: string;
  ag_phone: string;
  ag_company: string;
}

function boolToStr(v: boolean | null | undefined): string {
  if (v === true) return 'true';
  if (v === false) return 'false';
  return '';
}

function numToStr(v: number | null | undefined): string {
  if (v === null || v === undefined) return '';
  return String(v);
}

function initForm(offer: Offer): EditFormState {
  const ed = offer.extractedData;
  return {
    has_pre_approval: boolToStr(ed?._meta?.has_pre_approval),
    pa_lender_name: ed?.pre_approval?.lender_name || '',
    pa_lender_phone: ed?.pre_approval?.lender_phone || '',
    pa_loan_officer_name: ed?.pre_approval?.loan_officer_name || '',
    pa_loan_officer_nmls: ed?.pre_approval?.loan_officer_nmls ? String(ed.pre_approval.loan_officer_nmls) : '',
    pa_loan_officer_email: ed?.pre_approval?.loan_officer_email || '',
    pa_loan_type: ed?.pre_approval?.loan_type || '',
    pa_base_loan_amount: numToStr(ed?.pre_approval?.base_loan_amount),
    pa_max_purchase_price: numToStr(ed?.pre_approval?.max_purchase_price),
    pa_letter_date: ed?.pre_approval?.letter_date || '',
    pa_expiration_date: ed?.pre_approval?.expiration_date || '',
    t_final_sales_price: numToStr(ed?.transaction?.final_sales_price),
    t_earnest_money_deposit: numToStr(ed?.transaction?.earnest_money_deposit),
    t_closing_date: ed?.transaction?.closing_date || '',
    t_offer_date: ed?.transaction?.offer_date || '',
    f_payment_method: ed?.financing?.payment_method || '',
    f_mortgage_type: ed?.financing?.mortgage_type || '',
    f_mortgage_amount: numToStr(ed?.financing?.mortgage_amount),
    f_down_payment_amount: numToStr(ed?.financing?.down_payment_amount),
    f_down_payment_percent: numToStr(ed?.financing?.down_payment_percent),
    f_seller_concession_amount: numToStr(ed?.financing?.seller_concession_amount),
    c_appraisal: boolToStr(ed?.contingencies?.appraisal),
    c_inspection_waived: boolToStr(ed?.contingencies?.inspection?.waived),
    c_sale_of_buyers_property: boolToStr(ed?.contingencies?.sale_of_buyers_property),
    c_mortgage: boolToStr(ed?.contingencies?.mortgage),
    prop_address: ed?.property?.address || '',
    prop_city: ed?.property?.city || '',
    prop_state: ed?.property?.state || '',
    prop_zip: ed?.property?.zip || '',
    ag_agent_name: ed?.agents?.buyer_broker?.agent_name || '',
    ag_email: ed?.agents?.buyer_broker?.email || '',
    ag_phone: ed?.agents?.buyer_broker?.phone || '',
    ag_company: ed?.agents?.buyer_broker?.company || '',
  };
}

function strToBool(v: string): boolean | null {
  if (v === 'true') return true;
  if (v === 'false') return false;
  return null;
}

function strToNum(v: string): number | null {
  if (!v) return null;
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
}

const LOAN_TYPE_OPTIONS = ['Conventional', 'FHA', 'VA', 'USDA', 'Jumbo', 'Other'];
const PAYMENT_METHOD_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'cash_with_new_mortgage', label: 'New Mortgage' },
  { value: 'assumption', label: 'Assumption' },
  { value: 'seller_financing', label: 'Seller Financing' },
  { value: 'other', label: 'Other' },
];
const BOOL_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

type PaUploadStatus = 'idle' | 'uploading' | 'extracting' | 'done' | 'error';

const OfferEditModal: React.FC<OfferEditModalProps> = ({ offer, lenderExperienceYears, onClose, onSaved }) => {
  const [form, setForm] = useState<EditFormState>(() => initForm(offer));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>('pre_approval');
  const [paUploadStatus, setPaUploadStatus] = useState<PaUploadStatus>('idle');
  const [paUploadError, setPaUploadError] = useState<string | null>(null);
  const [paUploadProgress, setPaUploadProgress] = useState(0);
  const paFileInputRef = useRef<HTMLInputElement>(null);

  // Reset form if offer changes (shouldn't happen but safety)
  useEffect(() => { setForm(initForm(offer)); }, [offer.id]);

  function set(key: keyof EditFormState, value: string) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  // Build live PTCCalculationInput from current form state
  const livePtcInput = useMemo((): PTCCalculationInput => {
    const isCash = form.f_payment_method === 'cash';
    const hasPA = form.has_pre_approval === 'true';
    const loanType = form.pa_loan_type || form.f_mortgage_type || '';
    const loanAmount = strToNum(form.pa_base_loan_amount) ?? strToNum(form.f_mortgage_amount);
    const purchasePrice = strToNum(form.t_final_sales_price);
    const appraisalActive = form.c_appraisal === 'true';
    const inspectionActive = form.c_inspection_waived === 'false';
    const saleOfPropActive = form.c_sale_of_buyers_property === 'true';
    const inspectionWaived = form.c_inspection_waived === 'true';

    return {
      isCashOffer: isCash,
      inspectionWaived,
      hasPreApproval: hasPA,
      lenderExperienceYears: lenderExperienceYears ?? undefined,
      loanAmount: loanAmount ?? undefined,
      purchasePrice: purchasePrice ?? undefined,
      loanType,
      contingencies: {
        appraisalContingency: appraisalActive,
        homeInspectionContingency: inspectionActive,
        saleOfBuyerPropertyContingency: saleOfPropActive,
      },
    };
  }, [form, lenderExperienceYears]);

  const livePtcResult = useMemo(() => calculatePtcScore(livePtcInput), [livePtcInput]);
  const liveScore = Math.round(livePtcResult.ptcScore);

  const scoreColor = liveScore >= 70 ? '#22c55e' : liveScore >= 50 ? '#f59e0b' : '#ef4444';

  async function handlePaFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !offer.id) return;
    if (file.type !== 'application/pdf') {
      setPaUploadError('Please select a PDF file.');
      setPaUploadStatus('error');
      return;
    }

    setPaUploadStatus('uploading');
    setPaUploadError(null);
    setPaUploadProgress(0);

    try {
      // Upload PDF to Firebase Storage
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `offers/${offer.userId}/${timestamp}_${safeName}`;
      const storageRef = ref(storage, filePath);
      const uploadTask = uploadBytesResumable(storageRef, file, { contentType: 'application/pdf' });

      await new Promise<void>((resolve, reject) => {
        uploadTask.on('state_changed',
          snap => setPaUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          reject,
          () => resolve(),
        );
      });

      const downloadURL = await getDownloadURL(storageRef);

      // Call Cloud Function to re-extract with the new file
      setPaUploadStatus('extracting');
      const fn = httpsCallable(getFunctions(), 'reprocessOfferPdf');
      const result: any = await fn({
        offerId: offer.id,
        newFile: { filePath, fileName: file.name, downloadURL },
      });

      // Populate form fields from newly extracted pre-approval data
      if (result.data?.extractedData?.pre_approval) {
        const pa = result.data.extractedData.pre_approval;
        const meta = result.data.extractedData._meta;
        setForm(prev => ({
          ...prev,
          has_pre_approval: meta?.has_pre_approval === true ? 'true' : meta?.has_pre_approval === false ? 'false' : prev.has_pre_approval,
          pa_lender_name: pa.lender_name || prev.pa_lender_name,
          pa_lender_phone: pa.lender_phone || prev.pa_lender_phone,
          pa_loan_officer_name: pa.loan_officer_name || prev.pa_loan_officer_name,
          pa_loan_officer_nmls: pa.loan_officer_nmls ? String(pa.loan_officer_nmls) : prev.pa_loan_officer_nmls,
          pa_loan_officer_email: pa.loan_officer_email || prev.pa_loan_officer_email,
          pa_loan_type: pa.loan_type || prev.pa_loan_type,
          pa_base_loan_amount: pa.base_loan_amount != null ? String(pa.base_loan_amount) : prev.pa_base_loan_amount,
          pa_max_purchase_price: pa.max_purchase_price != null ? String(pa.max_purchase_price) : prev.pa_max_purchase_price,
          pa_letter_date: pa.letter_date || prev.pa_letter_date,
          pa_expiration_date: pa.expiration_date || prev.pa_expiration_date,
        }));
      }

      setPaUploadStatus('done');
    } catch (err: any) {
      console.error('Pre-approval upload failed:', err);
      setPaUploadError(err?.message || 'Upload failed. Please try again.');
      setPaUploadStatus('error');
    } finally {
      // Reset file input so the same file can be re-selected
      if (paFileInputRef.current) paFileInputRef.current.value = '';
    }
  }

  async function handleSave() {
    if (!offer.id) return;
    setSaving(true);
    setSaveError(null);

    try {
      const ptcResult = calculatePtcScore(livePtcInput);
      const payload: Record<string, any> = {
        updatedAt: Timestamp.now(),
        dataEditedManually: true,
        ptcScore: ptcResult.ptcScore,
        ptcBreakdown: ptcResult.ptcBreakdown,
        ptcCalculatedAt: Timestamp.now(),
        // _meta
        'extractedData._meta.has_pre_approval': form.has_pre_approval === 'true',
        // pre_approval
        'extractedData.pre_approval.lender_name': form.pa_lender_name,
        'extractedData.pre_approval.lender_phone': form.pa_lender_phone,
        'extractedData.pre_approval.loan_officer_name': form.pa_loan_officer_name,
        'extractedData.pre_approval.loan_officer_nmls': form.pa_loan_officer_nmls,
        'extractedData.pre_approval.loan_officer_email': form.pa_loan_officer_email,
        'extractedData.pre_approval.loan_type': form.pa_loan_type,
        'extractedData.pre_approval.base_loan_amount': strToNum(form.pa_base_loan_amount),
        'extractedData.pre_approval.max_purchase_price': strToNum(form.pa_max_purchase_price),
        'extractedData.pre_approval.letter_date': form.pa_letter_date,
        'extractedData.pre_approval.expiration_date': form.pa_expiration_date,
        // transaction
        'extractedData.transaction.final_sales_price': strToNum(form.t_final_sales_price),
        'extractedData.transaction.earnest_money_deposit': strToNum(form.t_earnest_money_deposit),
        'extractedData.transaction.closing_date': form.t_closing_date,
        'extractedData.transaction.offer_date': form.t_offer_date,
        // financing
        'extractedData.financing.payment_method': form.f_payment_method,
        'extractedData.financing.mortgage_type': form.f_mortgage_type,
        'extractedData.financing.mortgage_amount': strToNum(form.f_mortgage_amount),
        'extractedData.financing.down_payment_amount': strToNum(form.f_down_payment_amount),
        'extractedData.financing.down_payment_percent': strToNum(form.f_down_payment_percent),
        'extractedData.financing.seller_concession_amount': strToNum(form.f_seller_concession_amount),
        // contingencies
        'extractedData.contingencies.appraisal': strToBool(form.c_appraisal),
        'extractedData.contingencies.inspection.waived': strToBool(form.c_inspection_waived),
        'extractedData.contingencies.sale_of_buyers_property': strToBool(form.c_sale_of_buyers_property),
        'extractedData.contingencies.mortgage': strToBool(form.c_mortgage),
        // property
        'extractedData.property.address': form.prop_address,
        'extractedData.property.city': form.prop_city,
        'extractedData.property.state': form.prop_state,
        'extractedData.property.zip': form.prop_zip,
        // buyer agent
        'extractedData.agents.buyer_broker.agent_name': form.ag_agent_name,
        'extractedData.agents.buyer_broker.email': form.ag_email,
        'extractedData.agents.buyer_broker.phone': form.ag_phone,
        'extractedData.agents.buyer_broker.company': form.ag_company,
      };

      await updateDoc(doc(db, 'offers', offer.id), payload);

      // Build updated offer for parent state
      const updatedOffer: Offer = {
        ...offer,
        ptcScore: ptcResult.ptcScore,
        ptcBreakdown: ptcResult.ptcBreakdown,
        extractedData: {
          ...offer.extractedData,
          _meta: { ...offer.extractedData?._meta, has_pre_approval: form.has_pre_approval === 'true' } as any,
          pre_approval: {
            ...offer.extractedData?.pre_approval,
            lender_name: form.pa_lender_name,
            lender_phone: form.pa_lender_phone,
            loan_officer_name: form.pa_loan_officer_name,
            loan_officer_nmls: form.pa_loan_officer_nmls,
            loan_officer_email: form.pa_loan_officer_email,
            loan_type: form.pa_loan_type as any,
            base_loan_amount: strToNum(form.pa_base_loan_amount),
            max_purchase_price: strToNum(form.pa_max_purchase_price),
            letter_date: form.pa_letter_date,
            expiration_date: form.pa_expiration_date,
          } as any,
          transaction: {
            ...offer.extractedData?.transaction,
            final_sales_price: strToNum(form.t_final_sales_price),
            earnest_money_deposit: strToNum(form.t_earnest_money_deposit),
            closing_date: form.t_closing_date,
            offer_date: form.t_offer_date,
          } as any,
          financing: {
            ...offer.extractedData?.financing,
            payment_method: form.f_payment_method as any,
            mortgage_type: form.f_mortgage_type as any,
            mortgage_amount: strToNum(form.f_mortgage_amount),
            down_payment_amount: strToNum(form.f_down_payment_amount),
            down_payment_percent: strToNum(form.f_down_payment_percent),
            seller_concession_amount: strToNum(form.f_seller_concession_amount),
          } as any,
          contingencies: {
            ...offer.extractedData?.contingencies,
            appraisal: strToBool(form.c_appraisal),
            inspection: { ...offer.extractedData?.contingencies?.inspection, waived: strToBool(form.c_inspection_waived) } as any,
            sale_of_buyers_property: strToBool(form.c_sale_of_buyers_property),
            mortgage: strToBool(form.c_mortgage),
          } as any,
          property: {
            ...offer.extractedData?.property,
            address: form.prop_address,
            city: form.prop_city,
            state: form.prop_state,
            zip: form.prop_zip,
          } as any,
          agents: {
            ...offer.extractedData?.agents,
            buyer_broker: {
              ...offer.extractedData?.agents?.buyer_broker,
              agent_name: form.ag_agent_name,
              email: form.ag_email,
              phone: form.ag_phone,
              company: form.ag_company,
            } as any,
          } as any,
        } as any,
      };

      onSaved(updatedOffer);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  const sections = [
    { key: 'pre_approval', label: 'Pre-Approval' },
    { key: 'transaction', label: 'Transaction' },
    { key: 'financing', label: 'Financing' },
    { key: 'contingencies', label: 'Contingencies' },
    { key: 'property', label: 'Property' },
    { key: 'buyer_agent', label: 'Buyer Agent' },
  ];

  return (
    <div className="oem-overlay">
      <div className="oem-modal">
        {/* Header */}
        <div className="oem-header">
          <div className="oem-header-left">
            <h2 className="oem-title">Edit Offer Details</h2>
            <p className="oem-subtitle">{offer.extractedData?.property?.address || offer.buyerAgentName || 'Offer'}</p>
          </div>
          <div className="oem-header-right">
            <div className="oem-live-score" style={{ borderColor: scoreColor, color: scoreColor }}>
              <span className="oem-live-score-num">{liveScore}</span>
              <span className="oem-live-score-label">PTC Score</span>
            </div>
            <button className="oem-close-btn" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>

        {/* Section tabs */}
        <div className="oem-tabs">
          {sections.map(s => (
            <button
              key={s.key}
              className={`oem-tab${activeSection === s.key ? ' active' : ''}`}
              onClick={() => setActiveSection(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Form body */}
        <div className="oem-body">

          {/* ── PRE-APPROVAL ── */}
          {activeSection === 'pre_approval' && (
            <div className="oem-section">

              {/* PDF Upload Zone */}
              <div className={`oem-pa-upload${paUploadStatus === 'done' ? ' oem-pa-upload-done' : paUploadStatus === 'error' ? ' oem-pa-upload-error' : ''}`}>
                <input
                  ref={paFileInputRef}
                  type="file"
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  onChange={handlePaFileSelected}
                />
                {paUploadStatus === 'idle' && (
                  <button
                    type="button"
                    className="oem-pa-upload-btn"
                    onClick={() => paFileInputRef.current?.click()}
                  >
                    <span className="oem-pa-upload-icon">📄</span>
                    Upload Pre-Approval PDF
                    <span className="oem-pa-upload-hint">Auto-extracts fields below</span>
                  </button>
                )}
                {paUploadStatus === 'uploading' && (
                  <div className="oem-pa-progress">
                    <div className="oem-pa-progress-label">Uploading… {paUploadProgress}%</div>
                    <div className="oem-pa-progress-bar"><div className="oem-pa-progress-fill" style={{ width: `${paUploadProgress}%` }} /></div>
                  </div>
                )}
                {paUploadStatus === 'extracting' && (
                  <div className="oem-pa-progress">
                    <div className="oem-pa-progress-label"><span className="oem-pa-spinner" /> Extracting data from PDF…</div>
                  </div>
                )}
                {paUploadStatus === 'done' && (
                  <div className="oem-pa-result-row">
                    <span className="oem-pa-result-icon">✓</span>
                    <span className="oem-pa-result-text">Fields updated from PDF</span>
                    <button type="button" className="oem-pa-reupload-btn" onClick={() => { setPaUploadStatus('idle'); paFileInputRef.current?.click(); }}>Replace</button>
                  </div>
                )}
                {paUploadStatus === 'error' && (
                  <div className="oem-pa-result-row oem-pa-result-err">
                    <span className="oem-pa-result-icon">✕</span>
                    <span className="oem-pa-result-text">{paUploadError || 'Upload failed'}</span>
                    <button type="button" className="oem-pa-reupload-btn" onClick={() => { setPaUploadStatus('idle'); paFileInputRef.current?.click(); }}>Retry</button>
                  </div>
                )}
              </div>

              <div className="oem-field-row">
                <label className="oem-label">Pre-Approval Included?</label>
                <select className="oem-select" value={form.has_pre_approval} onChange={e => set('has_pre_approval', e.target.value)}>
                  <option value="">— Not Set —</option>
                  {BOOL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Lender Name</label>
                <input className="oem-input" value={form.pa_lender_name} onChange={e => set('pa_lender_name', e.target.value)} placeholder="e.g. First National Bank" />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Lender Phone</label>
                <input className="oem-input" value={form.pa_lender_phone} onChange={e => set('pa_lender_phone', e.target.value)} placeholder="(555) 000-0000" />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Loan Officer Name</label>
                <input className="oem-input" value={form.pa_loan_officer_name} onChange={e => set('pa_loan_officer_name', e.target.value)} placeholder="Full name" />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Loan Officer NMLS #</label>
                <input className="oem-input" value={form.pa_loan_officer_nmls} onChange={e => set('pa_loan_officer_nmls', e.target.value)} placeholder="e.g. 123456" />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Loan Officer Email</label>
                <input className="oem-input" type="email" value={form.pa_loan_officer_email} onChange={e => set('pa_loan_officer_email', e.target.value)} placeholder="lo@lender.com" />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Loan Type</label>
                <select className="oem-select" value={form.pa_loan_type} onChange={e => set('pa_loan_type', e.target.value)}>
                  <option value="">— Not Set —</option>
                  {LOAN_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Base Loan Amount</label>
                <input className="oem-input" type="number" min="0" step="1000" value={form.pa_base_loan_amount} onChange={e => set('pa_base_loan_amount', e.target.value)} placeholder="e.g. 400000" />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Max Purchase Price</label>
                <input className="oem-input" type="number" min="0" step="1000" value={form.pa_max_purchase_price} onChange={e => set('pa_max_purchase_price', e.target.value)} placeholder="e.g. 450000" />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Letter Date</label>
                <input className="oem-input" type="date" value={form.pa_letter_date} onChange={e => set('pa_letter_date', e.target.value)} />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Expiration Date</label>
                <input className="oem-input" type="date" value={form.pa_expiration_date} onChange={e => set('pa_expiration_date', e.target.value)} />
              </div>
            </div>
          )}

          {/* ── TRANSACTION ── */}
          {activeSection === 'transaction' && (
            <div className="oem-section">
              <div className="oem-field-row">
                <label className="oem-label">Purchase Price</label>
                <input className="oem-input" type="number" min="0" step="1000" value={form.t_final_sales_price} onChange={e => set('t_final_sales_price', e.target.value)} placeholder="e.g. 500000" />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Earnest Money Deposit</label>
                <input className="oem-input" type="number" min="0" step="500" value={form.t_earnest_money_deposit} onChange={e => set('t_earnest_money_deposit', e.target.value)} placeholder="e.g. 10000" />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Closing Date</label>
                <input className="oem-input" type="date" value={form.t_closing_date} onChange={e => set('t_closing_date', e.target.value)} />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Offer Date</label>
                <input className="oem-input" type="date" value={form.t_offer_date} onChange={e => set('t_offer_date', e.target.value)} />
              </div>
            </div>
          )}

          {/* ── FINANCING ── */}
          {activeSection === 'financing' && (
            <div className="oem-section">
              <div className="oem-field-row">
                <label className="oem-label">Payment Method</label>
                <select className="oem-select" value={form.f_payment_method} onChange={e => set('f_payment_method', e.target.value)}>
                  <option value="">— Not Set —</option>
                  {PAYMENT_METHOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Mortgage Type <span className="oem-hint">(fallback if no pre-approval)</span></label>
                <select className="oem-select" value={form.f_mortgage_type} onChange={e => set('f_mortgage_type', e.target.value)}>
                  <option value="">— Not Set —</option>
                  {LOAN_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Mortgage Amount</label>
                <input className="oem-input" type="number" min="0" step="1000" value={form.f_mortgage_amount} onChange={e => set('f_mortgage_amount', e.target.value)} placeholder="e.g. 400000" />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Down Payment ($)</label>
                <input className="oem-input" type="number" min="0" step="1000" value={form.f_down_payment_amount} onChange={e => set('f_down_payment_amount', e.target.value)} placeholder="e.g. 100000" />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Down Payment (%)</label>
                <input className="oem-input" type="number" min="0" max="100" step="0.5" value={form.f_down_payment_percent} onChange={e => set('f_down_payment_percent', e.target.value)} placeholder="e.g. 20" />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Seller Concession ($)</label>
                <input className="oem-input" type="number" min="0" step="500" value={form.f_seller_concession_amount} onChange={e => set('f_seller_concession_amount', e.target.value)} placeholder="e.g. 5000" />
              </div>
            </div>
          )}

          {/* ── CONTINGENCIES ── */}
          {activeSection === 'contingencies' && (
            <div className="oem-section">
              <p className="oem-section-note">Active contingencies reduce the PTC score. "Yes" means the contingency is present/active.</p>
              <div className="oem-field-row">
                <label className="oem-label">Appraisal Contingency</label>
                <select className="oem-select" value={form.c_appraisal} onChange={e => set('c_appraisal', e.target.value)}>
                  <option value="">— Not Set —</option>
                  {BOOL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Inspection Waived?</label>
                <select className="oem-select" value={form.c_inspection_waived} onChange={e => set('c_inspection_waived', e.target.value)}>
                  <option value="">— Not Set —</option>
                  {BOOL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Mortgage Contingency</label>
                <select className="oem-select" value={form.c_mortgage} onChange={e => set('c_mortgage', e.target.value)}>
                  <option value="">— Not Set —</option>
                  {BOOL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Sale of Buyer's Property Contingency</label>
                <select className="oem-select" value={form.c_sale_of_buyers_property} onChange={e => set('c_sale_of_buyers_property', e.target.value)}>
                  <option value="">— Not Set —</option>
                  {BOOL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              {/* Score impact preview */}
              <div className="oem-score-breakdown">
                <h4 className="oem-breakdown-title">Score Breakdown Preview</h4>
                <div className="oem-breakdown-row">
                  <span>Path</span>
                  <span>
                    {livePtcInput.isCashOffer ? 'Cash offer' : livePtcInput.hasPreApproval === false ? 'No pre-approval (flat 40)' : 'Financed with pre-approval'}
                  </span>
                </div>
                {!livePtcInput.isCashOffer && livePtcInput.hasPreApproval !== false && (
                  <>
                    <div className="oem-breakdown-row">
                      <span>Lender experience</span>
                      <span>+{livePtcResult.ptcBreakdown.lenderExperiencePoints} pts ({livePtcResult.ptcBreakdown.lenderExperienceYears ?? '?'}yr{(livePtcResult.ptcBreakdown.lenderExperienceYears ?? 0) !== 1 ? 's' : ''})</span>
                    </div>
                    <div className="oem-breakdown-row">
                      <span>LTV / Loan type</span>
                      <span>+{livePtcResult.ptcBreakdown.ltvPoints} pts (LTV {livePtcResult.ptcBreakdown.ltvPercent !== null ? livePtcResult.ptcBreakdown.ltvPercent.toFixed(1) + '%' : 'N/A'}, {livePtcResult.ptcBreakdown.loanType || 'N/A'})</span>
                    </div>
                    {livePtcResult.ptcBreakdown.totalContingencyDeduction > 0 && (
                      <div className="oem-breakdown-row deduction">
                        <span>Contingency deductions</span>
                        <span>−{livePtcResult.ptcBreakdown.totalContingencyDeduction} pts</span>
                      </div>
                    )}
                  </>
                )}
                <div className="oem-breakdown-total" style={{ color: scoreColor }}>
                  <span>PTC Score</span>
                  <span>{liveScore}</span>
                </div>
              </div>
            </div>
          )}

          {/* ── PROPERTY ── */}
          {activeSection === 'property' && (
            <div className="oem-section">
              <div className="oem-field-row">
                <label className="oem-label">Street Address</label>
                <input className="oem-input" value={form.prop_address} onChange={e => set('prop_address', e.target.value)} placeholder="e.g. 123 Main St" />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">City</label>
                <input className="oem-input" value={form.prop_city} onChange={e => set('prop_city', e.target.value)} />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">State</label>
                <input className="oem-input" value={form.prop_state} onChange={e => set('prop_state', e.target.value)} placeholder="e.g. MI" maxLength={2} />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">ZIP Code</label>
                <input className="oem-input" value={form.prop_zip} onChange={e => set('prop_zip', e.target.value)} />
              </div>
            </div>
          )}

          {/* ── BUYER AGENT ── */}
          {activeSection === 'buyer_agent' && (
            <div className="oem-section">
              <div className="oem-field-row">
                <label className="oem-label">Agent Name</label>
                <input className="oem-input" value={form.ag_agent_name} onChange={e => set('ag_agent_name', e.target.value)} />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Agent Email</label>
                <input className="oem-input" type="email" value={form.ag_email} onChange={e => set('ag_email', e.target.value)} />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Agent Phone</label>
                <input className="oem-input" value={form.ag_phone} onChange={e => set('ag_phone', e.target.value)} />
              </div>
              <div className="oem-field-row">
                <label className="oem-label">Brokerage / Company</label>
                <input className="oem-input" value={form.ag_company} onChange={e => set('ag_company', e.target.value)} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="oem-footer">
          {saveError && <p className="oem-save-error">{saveError}</p>}
          <button className="oem-cancel-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="oem-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : `Save & Recalculate Score`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OfferEditModal;
